// apiService.ts - Updated for Region support
import { Gate, Region, GateUser, GateFormData, RegionFormData } from './types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

export class GateApiService {
  constructor(private getAccessToken: () => Promise<string>) {}

  // Gate Users API - for approver/reviewer selection
  async loadGateUsers(searchTerm: string = ''): Promise<{ success: boolean; data?: GateUser[]; message?: string }> {
    try {
      const token = await this.getAccessToken();
      const queryParams = new URLSearchParams();
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      const url = `${API_BASE_URL}/gate-users${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error(`Gate users fetch failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        return { success: true, data: data.data };
      } else {
        return { success: false, message: data.message || 'Failed to load gate users' };
      }
    } catch (error) {
      console.error('Gate users load error', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Failed to load gate users' 
      };
    }
  }

  // Region API operations (replaces Location operations)
  async loadRegions(searchTerm: string = ''): Promise<{ success: boolean; data?: Region[]; message?: string }> {
    try {
      const token = await this.getAccessToken();
      const queryParams = new URLSearchParams();
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      const url = `${API_BASE_URL}/regions${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error(`Regions fetch failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        return { success: true, data: data.data };
      } else {
        return { success: false, message: data.message || 'Failed to load regions' };
      }
    } catch (error) {
      console.error('Regions load error', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Failed to load regions' 
      };
    }
  }

  async createRegion(formData: RegionFormData): Promise<{ success: boolean; message?: string }> {
    try {
      const token = await this.getAccessToken();
      const response = await fetch(`${API_BASE_URL}/regioncreate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        return { success: true, message: 'Region created successfully' };
      } else {
        return { 
          success: false, 
          message: data.message || `Operation failed (${response.status})` 
        };
      }
    } catch (error) {
      console.error('Create region error:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Operation failed' 
      };
    }
  }

  async updateRegion(regionId: number, formData: RegionFormData): Promise<{ success: boolean; message?: string }> {
    try {
      const token = await this.getAccessToken();
      const response = await fetch(`${API_BASE_URL}/regionupdate/${regionId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        return { success: true, message: 'Region updated successfully' };
      } else {
        return { 
          success: false, 
          message: data.message || `Operation failed (${response.status})` 
        };
      }
    } catch (error) {
      console.error('Update region error:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Operation failed' 
      };
    }
  }

  async deleteRegion(region: Region): Promise<{ success: boolean; message?: string }> {
    try {
      const token = await this.getAccessToken();
      const response = await fetch(`${API_BASE_URL}/regiondelete/${region.regionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        return { success: true, message: `Region "${region.regionName}" deleted successfully` };
      } else {
        return { 
          success: false, 
          message: data.message || `Delete failed (${response.status})` 
        };
      }
    } catch (error) {
      console.error('Delete region error:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Delete failed' 
      };
    }
  }

  // Gate API operations (now with regionId)
  async loadGates(regionId: number, searchTerm: string = ''): Promise<{ success: boolean; data?: Gate[]; message?: string }> {
    if (!regionId) {
      return { success: true, data: [] };
    }
    
    try {
      const token = await this.getAccessToken();
      const queryParams = new URLSearchParams();
      queryParams.append('regionId', regionId.toString()); // Changed from locationId
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      const url = `${API_BASE_URL}/gates?${queryParams.toString()}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error(`Gates fetch failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        const searchSuffix = searchTerm ? ` (filtered by "${searchTerm}")` : '';
        return { 
          success: true, 
          data: data.data,
          message: `Loaded ${data.data.length} gates${searchSuffix}`
        };
      } else {
        return { success: false, message: data.message || 'Failed to load gates' };
      }
    } catch (error) {
      console.error('Gates load error', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Failed to load gates' 
      };
    }
  }

  async createGate(formData: GateFormData): Promise<{ success: boolean; message?: string }> {
    try {
      const token = await this.getAccessToken();
      const response = await fetch(`${API_BASE_URL}/gatecreate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        return { success: true, message: 'Gate created successfully' };
      } else {
        return { 
          success: false, 
          message: data.message || `Operation failed (${response.status})` 
        };
      }
    } catch (error) {
      console.error('Create gate error:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Operation failed' 
      };
    }
  }

  async updateGate(gateId: number, formData: GateFormData): Promise<{ success: boolean; message?: string }> {
    try {
      const token = await this.getAccessToken();
      const response = await fetch(`${API_BASE_URL}/gateupdate/${gateId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        return { success: true, message: 'Gate updated successfully' };
      } else {
        return { 
          success: false, 
          message: data.message || `Operation failed (${response.status})` 
        };
      }
    } catch (error) {
      console.error('Update gate error:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Operation failed' 
      };
    }
  }

  async deleteGate(gate: Gate): Promise<{ success: boolean; message?: string }> {
    try {
      const token = await this.getAccessToken();
      const response = await fetch(`${API_BASE_URL}/gatedelete/${gate.gateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        return { success: true, message: `Gate "${gate.gateNumber}" deleted successfully` };
      } else {
        return { 
          success: false, 
          message: data.message || `Delete failed (${response.status})` 
        };
      }
    } catch (error) {
      console.error('Delete gate error:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Delete failed' 
      };
    }
  }
}